import { useState } from 'react'

import { useAppStore } from '@core/store'
import {
  leituraGuardada,
  sitioGuardado,
  type AlternativaDoMapa,
  type FonteDoMapa,
} from '@viewer/lib/georef/leituraDoMapa'
import { colocacaoPeloSitio } from '@viewer/lib/georef/colocacaoPeloSitio'
import { formatCoord } from '@viewer/lib/modelInfo'

const NOME_DA_FONTE: Record<FonteDoMapa, string> = {
  IFCMAPCONVERSION: 'IfcMapConversion',
  IFCMAPCONVERSIONSCALED: 'IfcMapConversionScaled',
  EPSET_MAPCONVERSION: 'ePset_MapConversion (IFC2X3)',
}

const ORIGEM_DA_UNIDADE = {
  MapUnit: 'MapUnit',
  propriedade: 'declarada nas propriedades',
  projecto: 'do projecto, sem MapUnit',
} as const

/** Graus decimais em graus, minutos e segundos (só para ler). */
function gms(v: number): string {
  const a = Math.abs(v)
  const g = Math.floor(a)
  const m = Math.floor((a - g) * 60)
  const sec = (a - g - m / 60) * 3600
  return `${v < 0 ? '-' : ''}${g}° ${m}' ${formatCoord(sec, 2)}"`
}

/** Como o viewer leu a conversão para o mapa: de onde veio cada unidade,
 *  o que se aplicou e porquê, e, quando o ficheiro se contradiz, as duas
 *  leituras possíveis para a pessoa escolher. */
export default function LeituraDoMapaView({ modelId, comCabecalho }: { modelId: string; comCabecalho?: boolean }) {
  const setGeoOverride = useAppStore((s) => s.setGeoOverride)
  const override = useAppStore((s) => s.geoOverrides[modelId] ?? null)
  const l = leituraGuardada(modelId)
  if (!l) return null

  const aplicar = (a: AlternativaDoMapa) =>
    setGeoOverride(modelId, {
      eastings: a.origemEmMetros[0],
      northings: a.origemEmMetros[1],
      orthogonalHeight: a.origemEmMetros[2],
      rotationDeg: l.rotacaoGraus,
      scale: a.escalaGeometrica,
    })
  const escolhida = (a: AlternativaDoMapa) =>
    override !== null &&
    Math.abs(override.scale - a.escalaGeometrica) < 1e-9 &&
    Math.abs(override.eastings - a.origemEmMetros[0]) < 1e-6

  const c = l.conversao
  return (
    <div className="mt-2 space-y-1.5 text-[11px]">
      {comCabecalho && (
        <div className="rounded border border-emerald-700/50 bg-[var(--color-ok-bg)] px-2 py-1">
          <div className="text-[10px] uppercase tracking-wide text-emerald-400/90">
            {NOME_DA_FONTE[c.classe]} (#{c.id}){l.escalaGeometrica !== null ? ' · aplicado' : ''}
          </div>
          <div className="mt-0.5 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
            <span className="text-[var(--color-text-muted)]">CRS</span>
            <span className="font-mono">{l.crs?.nome ? `${l.crs.nome} (#${l.crs.id})` : 'não declarado'}</span>
            <span className="text-[var(--color-text-muted)]">Eastings</span>
            <span className="font-mono">{formatCoord(c.eastings)}</span>
            <span className="text-[var(--color-text-muted)]">Northings</span>
            <span className="font-mono">{formatCoord(c.northings)}</span>
            <span className="text-[var(--color-text-muted)]">Altura ortogonal</span>
            <span className="font-mono">{formatCoord(c.altura)}</span>
            <span className="text-[var(--color-text-muted)]">Rotação X</span>
            <span className="font-mono">
              {formatCoord(c.xAbscissa, 6)}, {formatCoord(c.xOrdenada, 6)} ({formatCoord(l.rotacaoGraus, 4)}°, calculado)
            </span>
            <span className="text-[var(--color-text-muted)]">Scale</span>
            <span className="font-mono">{c.scale === null ? 'omitido (1,0)' : formatCoord(c.scale, 6)}</span>
          </div>
        </div>
      )}
      <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
        {l.crsInfo && (
          <>
            <span className="text-[var(--color-text-muted)]">CRS</span>
            <span title="Do índice EPSG incluído (base de dados do PROJ)">{l.crsInfo.codigo}: {l.crsInfo.nome}</span>
            <span className="text-[var(--color-text-muted)]">Para WGS 84</span>
            <span title="A transformação usada para pôr o modelo no mapa e no globo, com a precisão que o EPSG lhe dá">
              {l.crsInfo.transformacao || 'nenhuma transformação de Helmert conhecida'}
            </span>
          </>
        )}
        <span className="text-[var(--color-text-muted)]">Unidade do projecto</span>
        <span className="font-mono">
          {l.unidadeDoProjecto ? `${l.unidadeDoProjecto.simbolo} (#${l.unidadeDoProjecto.id})` : 'não se sabe'}
        </span>
        <span className="text-[var(--color-text-muted)]">Unidade do mapa</span>
        <span className="font-mono">
          {l.unidadeDoMapa
            ? `${l.unidadeDoMapa.simbolo} (#${l.unidadeDoMapa.id}, ${ORIGEM_DA_UNIDADE[l.unidadeDoMapa.origem]})`
            : 'não se sabe'}
        </span>
        <span className="text-[var(--color-text-muted)]">Escala aplicada</span>
        <span className="font-mono" title="Scale x metros por unidade do mapa / metros por unidade do projecto (calculado)">
          {l.escalaGeometrica !== null ? `${formatCoord(l.escalaGeometrica, 6)} (calculado)` : 'nenhuma'}
        </span>
        <span className="text-[var(--color-text-muted)]">Origem em metros</span>
        <span className="font-mono" title="Eastings, Northings e OrthogonalHeight x metros por unidade do mapa (calculado)">
          {l.origemEmMetros ? `${l.origemEmMetros.map((v) => formatCoord(v, 3)).join(', ')} (calculado)` : 'nenhuma'}
        </span>
      </div>
      {l.avisos.length > 0 && (
        <ul className="space-y-0.5 rounded border border-[var(--color-border)] bg-[var(--color-bg)]/40 px-2 py-1 text-[10px] text-[var(--color-text-muted)]">
          {l.avisos.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      )}
      {l.alternativas.length > 0 && (
        <div className="rounded border border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] px-2 py-1.5 text-[var(--color-warn-text)]">
          <div className="mb-1 text-[10px] uppercase tracking-wide">O ficheiro contradiz-se: escolhe a leitura</div>
          <div className="space-y-1">
            {l.alternativas.map((a) => (
              <div key={a.rotulo} className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div>
                    {a.rotulo}
                    {a.foraDaArea && <span className="ml-1 font-medium">(fora da área de uso do CRS)</span>}
                  </div>
                  <div className="font-mono text-[10px]">
                    escala {formatCoord(a.escalaGeometrica, 6)}, origem {a.origemEmMetros.map((v) => formatCoord(v, 2)).join(', ')} m
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => aplicar(a)}
                  disabled={escolhida(a)}
                  className="shrink-0 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-0.5 text-[10px] text-[var(--color-text)] hover:border-[var(--color-accent)] disabled:opacity-50"
                >
                  {escolhida(a) ? 'Em uso' : 'Usar'}
                </button>
              </div>
            ))}
          </div>
          <div className="mt-1 text-[10px]">
            A escolha fica como posição manual do modelo (painel Posição) e pode desfazer-se lá. O ficheiro não muda.
          </div>
        </div>
      )}
    </div>
  )
}

/** O IfcSite com latitude e longitude, quando o ficheiro não tem outra
 *  georreferenciação: o que declara, o que o viewer calculou com isso, e
 *  um botão para o usar quando não se aplicou sozinho. */
export function SitioView({ modelId }: { modelId: string }) {
  const s = sitioGuardado(modelId)
  const setGeoOverride = useAppStore((st) => st.setGeoOverride)
  const setCrsOverride = useAppStore((st) => st.setCrsOverride)
  const [aAplicar, setAAplicar] = useState(false)
  if (!s) return null
  const usar = async () => {
    setAAplicar(true)
    try {
      const c = await colocacaoPeloSitio(s)
      if (!c) {
        useAppStore.getState().pushToast('Não foi possível obter a definição do fuso UTM.', { type: 'error' })
        return
      }
      setCrsOverride(modelId, c.epsg)
      setGeoOverride(modelId, c.placement)
      useAppStore.getState().pushToast(`Modelo posto na latitude e longitude do IfcSite #${s.id} (${c.epsg}, calculado).`, { type: 'success' })
    } finally {
      setAAplicar(false)
    }
  }
  return (
    <div className="space-y-1.5 text-[11px]">
      <div className="rounded border border-[var(--color-border)] px-2 py-1">
        <div className="text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
          IfcSite #{s.id}: latitude e longitude{s.aplicado ? ' · aplicado' : ''}
        </div>
        <div className="mt-0.5 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
          <span className="text-[var(--color-text-muted)]">Latitude</span>
          <span className="font-mono">{formatCoord(s.latitude, 7)}° ({gms(s.latitude)})</span>
          <span className="text-[var(--color-text-muted)]">Longitude</span>
          <span className="font-mono">{formatCoord(s.longitude, 7)}° ({gms(s.longitude)})</span>
          <span className="text-[var(--color-text-muted)]">Elevação</span>
          <span className="font-mono">{s.elevacaoMetros === null ? 'não declarada' : `${formatCoord(s.elevacaoMetros, 3)} m`}</span>
          <span className="text-[var(--color-text-muted)]">Norte verdadeiro</span>
          <span className="font-mono">
            {s.trueNorthGraus === null ? 'TrueNorth omitido: +Y é o norte' : `${formatCoord(s.trueNorthGraus, 4)}° a partir de +Y (TrueNorth)`}
          </span>
          {s.aplicado && (
            <>
              <span className="text-[var(--color-text-muted)]">Grelha</span>
              <span className="font-mono" title="O fuso UTM onde a longitude cai. Não muda o sítio onde o modelo fica, só a grelha em que se desenha.">
                {s.aplicado.epsg} (calculado)
              </span>
              <span className="text-[var(--color-text-muted)]">Convergência</span>
              <span className="font-mono" title="Ângulo entre o norte verdadeiro e o norte da quadrícula naquele ponto, somado à rotação.">
                {formatCoord(s.aplicado.convergenciaGraus, 4)}° (calculado)
              </span>
            </>
          )}
        </div>
      </div>
      {s.avisos.length > 0 && (
        <ul className="space-y-0.5 rounded border border-[var(--color-border)] bg-[var(--color-bg)]/40 px-2 py-1 text-[10px] text-[var(--color-text-muted)]">
          {s.avisos.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      )}
      {!s.aplicado && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void usar()}
            disabled={aAplicar}
            className="shrink-0 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-0.5 text-[10px] text-[var(--color-text)] hover:border-[var(--color-accent)] disabled:opacity-50"
          >
            Pôr no mapa por esta latitude/longitude
          </button>
          <span className="text-[10px] text-[var(--color-text-muted)]">
            Não se aplicou sozinho porque o modelo já tem coordenadas de CRS embutidas (a mais de 10 km da origem). Fica como posição manual.
          </span>
        </div>
      )}
    </div>
  )
}
